import React, { useEffect, useMemo, useState } from 'react';
import CustomSelect from '../components/shared/CustomSelect';
import { getfetchSites, createSite, fetchPeriodical, fetchTickets } from '../utils/api';
import { fmtShort, TIME_LABELS, timeToLabel, labelToTime, melTodayISO } from '../utils/melDate';
import { getCurrentMonthFilter } from '../components/hooks/useFilterData';
import { SUBURBS, findSuburb, suburbLabel } from '../data/vicSuburbs';
import { useSuper, JobDetailModal, addrLine } from './SupervisorAvailability';
import { FoldGroup, FOLD_PEEK } from '../components/shared/FoldGroup';
import { useLockBody } from '../components/MessagesPanel';
import VideoAvailableLibrary from '../components/VideoAvailableLibrary';

/**
 * CreateCasualJobs — first supervisor tab, before Open Slots.
 *
 * Four buttons (one active at a time) open an inline picker:
 *   New Client  → small site form (SitesManagement's create), then Use Job
 *   Existing    → SitesManagement-style Active / Non Active search + cards, Use Job per card
 *   Periodical  → FilterPeriodical data (fetchPeriodical), search, Use Job per card
 *   Ticket      → FilterTickets data (fetchTickets), search, Use Job per card
 * "Use Job" pre-fills a job draft (name, type, address, suburb, times, date,
 * contact, mobile, notes); saving POSTs to Jobs Available (endpointsnljobs)
 * and the table at the bottom refreshes. The Assign form's Job Name
 * autocomplete reads that same table.
 */

const ACCENT = '#22d3ee';
const JOB_TYPES = ['New Client', 'Existing', 'Periodical', 'Ticket'];
const CLOSED = 'Closed Jobs';
const SUBURB_LABELS = SUBURBS.map(suburbLabel);
const OPERATIONAL_OPTIONS = ['Active', 'Non Active'];

/* guess the suburb from an address string: longest suburb name it contains */
const guessSuburb = (address) => {
  const a = String(address || '').toLowerCase();
  if (!a) return null;
  let best = null;
  for (const s of SUBURBS) {
    if (a.includes(s.s.toLowerCase()) && (!best || s.s.length > best.s.length)) best = s;
  }
  return best;
};

/* sites by name (case-insensitive) for periodical / ticket address lookups */
const siteByName = (sites, name) => {
  const n = String(name || '').trim().toLowerCase();
  return n ? sites.find((s) => (s.name || '').trim().toLowerCase() === n) || null : null;
};

/* ── draft builders ──────────────────────────────────────────── */
const draftFromSite = (site, jobType) => ({
  jobType, jobName: site.name || '',
  jobAddress: site.address || '', jobSuburb: guessSuburb(site.address),
  jobStart: '', jobEnd: '', jobStartDate: '',
  contactPerson: site.contact || '', contactMobile: site.mobile || '', notes: '',
  sourceRecordId: site.recordId || '', sourceTable: 'Sites',
});
const draftFromPeriodical = (item, sites) => {
  const site = siteByName(sites, item[4]);
  return {
    jobType: 'Periodical', jobName: (item[4] || '').trim(),          // site only
    jobAddress: site?.address || '', jobSuburb: guessSuburb(site?.address),
    jobStart: '', jobEnd: '', jobStartDate: item[2] || '',
    contactPerson: site?.contact || '', contactMobile: site?.mobile || '',
    // what / how goes in Notes
    notes: [item[1] ? `Periodical: ${item[1]}` : '', item[19] ? `Cycle: ${item[19]}` : '', item[13] ? `Attending: ${item[13]}` : '', item[15] ? `${item[15]} days` : ''].filter(Boolean).join('\n'),
    sourceRecordId: item[0] || '', sourceTable: 'Periodicals',
  };
};
const draftFromTicket = (item, sites) => {
  const site = siteByName(sites, item[1]);
  return {
    jobType: 'Ticket', jobName: (item[1] || '').trim(),              // site (client) only
    jobAddress: site?.address || '', jobSuburb: guessSuburb(site?.address),
    jobStart: '', jobEnd: '', jobStartDate: item[3] || '',
    contactPerson: site?.contact || '', contactMobile: site?.mobile || '',
    notes: [item[9] ? `Ticket: ${item[9]}` : '', item[2] ? `Type: ${item[2]}` : '', item[10] || ''].filter(Boolean).join('\n'),
    sourceRecordId: item[0] || '', sourceTable: 'Tickets',
  };
};

/* the casual's window for the day. Every assignment carries it (Slot Start / Slot End), so it
   shows through Pending → Accepted → Approved; an older shape falls back to the joined board slot. */
const cleanerWindow = (a) => ((a?.slotStart || a?.slotEnd) ? { start: a.slotStart, end: a.slotEnd }
  : (a?.slot ? { start: a.slot.start, end: a.slot.end } : null));
const cleanerTimes = (a) => { const w = cleanerWindow(a); return w ? `${timeToLabel(w.start)} – ${timeToLabel(w.end)}` : ''; };

/* Duplicate New: a closed job becomes a fresh draft in the Job details form. A past start
   date is dropped (the form only takes today onward); everything else carries over. */
const draftFromJob = (j) => ({
  jobType: JOB_TYPES.includes(j.jobType) ? j.jobType : 'Existing', jobName: j.jobName || '',
  jobAddress: j.jobAddress || '',
  jobSuburb: findSuburb(`${j.jobSuburb || ''} ${j.jobPostcode || ''}`) || findSuburb(j.jobSuburb) || guessSuburb(j.jobAddress),
  jobStart: j.jobStart || '', jobEnd: j.jobEnd || '',
  jobStartDate: j.jobStartDate && j.jobStartDate >= melTodayISO() ? j.jobStartDate : '',
  contactPerson: j.contactPerson || '', contactMobile: j.contactMobile || '', notes: j.notes || '',
  sourceRecordId: j.sourceRecordId || '', sourceTable: j.sourceTable || '',
});

/* ── month navigation for FilterTemplate-style fetches ─────────
   fetchPeriodical / fetchTickets take { monthYear } ("Sep2026") and return
   { status: 'success', periodicals | tickets: [...], total }. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shiftMonth = (monthYear, n) => {
  const m = MONTHS.indexOf(monthYear.slice(0, -4));
  const y = Number(monthYear.slice(-4));
  const d = new Date(y, m + n, 1);
  return `${MONTHS[d.getMonth()]}${d.getFullYear()}`;
};
const monthLabel = (monthYear) => `${monthYear.slice(0, -4)} ${monthYear.slice(-4)}`;
const MonthNav = ({ value, onChange }) => (
  <div className="sv-monthnav">
    <button type="button" className="sv-chip" onClick={() => onChange(shiftMonth(value, -1))} aria-label="Previous month">‹</button>
    <span className="sv-month">{monthLabel(value)}</span>
    <button type="button" className="sv-chip" onClick={() => onChange(shiftMonth(value, 1))} aria-label="Next month">›</button>
    {value !== getCurrentMonthFilter() && <button type="button" className="ca-link" onClick={() => onChange(getCurrentMonthFilter())}>this month</button>}
  </div>
);
const useMonthData = (fetcher, key) => {
  const [monthYear, setMonthYear] = useState(getCurrentMonthFilter());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetcher({ monthYear })
      .then((r) => { if (alive) setItems(r?.status === 'success' ? (r[key] || []) : []); })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [monthYear]);
  return { monthYear, setMonthYear, items, loading };
};

/* ── shared bits ─────────────────────────────────────────────── */
const Search = ({ value, onChange, placeholder }) => (
  <input className="sv-search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
);
const UseJobBtn = ({ onClick }) => (
  <button type="button" className="ca-btn solid sv-use" style={{ background: ACCENT }} onClick={(e) => { e.stopPropagation(); onClick(); }}>Use Job</button>
);

/* ── 1. New Client ───────────────────────────────────────────── */
const NewClientPanel = ({ onUse, onSiteCreated }) => {
  const [f, setF] = useState({ name: '', address: '', contact: '', mobile: '', operational: 'Active' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const valid = ['name', 'address', 'contact', 'mobile'].every((k) => f[k].trim());

  const create = async () => {
    setErr(''); setBusy(true);
    try {
      const res = await createSite({ name: f.name.trim(), address: f.address.trim(), contact: f.contact.trim(), mobile: f.mobile.trim(), operational: f.operational });
      if (res.status !== 'success') throw new Error(res.error || 'Create failed');
      const site = { recordId: res.recordId, ...f, name: f.name.trim() };
      onSiteCreated(site);
      onUse(draftFromSite(site, 'New Client'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="sv-panel">
      <div className="sv-panel-title">New client site</div>
      <div className="sv-form">
        <label className="sv-field sv-wide"><span className="ca-label">Site name</span><input value={f.name} onChange={(e) => set('name')(e.target.value)} /></label>
        <label className="sv-field sv-wide"><span className="ca-label">Site address</span><input value={f.address} onChange={(e) => set('address')(e.target.value)} /></label>
        <label className="sv-field"><span className="ca-label">Site contact</span><input value={f.contact} onChange={(e) => set('contact')(e.target.value)} /></label>
        <label className="sv-field"><span className="ca-label">Site mobile</span><input value={f.mobile} onChange={(e) => set('mobile')(e.target.value)} inputMode="tel" /></label>
        <div className="sv-field ob-field"><CustomSelect label="Operational" value={f.operational} onChange={set('operational')} options={OPERATIONAL_OPTIONS} /></div>
        {err && <div className="ca-error sv-wide">{err}</div>}
      </div>
      <div className="sv-panel-foot">
        <button type="button" className="ca-btn solid" style={{ background: ACCENT }} disabled={!valid || busy} onClick={create}>{busy ? 'Creating…' : 'Create client and use job'}</button>
      </div>
    </div>
  );
};

/* ── 2. Existing sites ───────────────────────────────────────── */
const ExistingSitesPanel = ({ sites, loading, onUse }) => {
  const [status, setStatus] = useState('Active');
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return sites
      .filter((s) => (status === 'Active' ? s.operational === 'Active' : s.operational !== 'Active'))
      .filter((s) => !n || (s.name || '').toLowerCase().includes(n) || (s.address || '').toLowerCase().includes(n));
  }, [sites, status, q]);
  const counts = useMemo(() => ({ Active: sites.filter((s) => s.operational === 'Active').length, 'Non Active': sites.filter((s) => s.operational !== 'Active').length }), [sites]);
  return (
    <div className="sv-panel">
      <div className="sv-panel-title">Existing sites <span className="sv-day-count">{list.length} of {counts[status]}</span></div>
      <div className="sv-ranges">
        {OPERATIONAL_OPTIONS.map((s) => (
          <button key={s} type="button" className={`sv-chip ${status === s ? 'on' : ''}`} style={status === s ? { borderColor: ACCENT, color: ACCENT } : undefined} onClick={() => setStatus(s)}>{s} · {counts[s]}</button>
        ))}
      </div>
      <Search value={q} onChange={setQ} placeholder="Search by site name or address…" />
      {loading && <div className="ca-skeleton" />}
      <div className="sv-cards">
        {list.map((s) => (
          <div key={s.recordId} className="sv-card" onClick={() => onUse(draftFromSite(s, 'Existing'))}>
            <div className="sv-card-name">{s.name}</div>
            <div className="sv-card-line">{s.address || '—'}</div>
            <div className="sv-card-line sv-card-muted">{s.contact || '—'} · {s.mobile || '—'}</div>
            <div className="sv-card-foot"><span className={`ca-avail-status ${s.operational === 'Active' ? 'open' : 'expired'}`}>{s.operational}</span><UseJobBtn onClick={() => onUse(draftFromSite(s, 'Existing'))} /></div>
          </div>
        ))}
        {!loading && list.length === 0 && <div className="ca-no-jobs">No {status.toLowerCase()} sites match.</div>}
      </div>
    </div>
  );
};

/* ── 3. Periodical ───────────────────────────────────────────── */
const PeriodicalPanel = ({ sites, onUse }) => {
  const { monthYear, setMonthYear, items, loading } = useMonthData(fetchPeriodical, 'periodicals');
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items.filter((it) => !n || [it[1], it[4], it[19], it[21]].some((v) => String(v || '').toLowerCase().includes(n)));
  }, [items, q]);
  return (
    <div className="sv-panel">
      <div className="sv-panel-title">Periodical work <span className="sv-day-count">{list.length}</span></div>
      <MonthNav value={monthYear} onChange={setMonthYear} />
      <Search value={q} onChange={setQ} placeholder="Search by site, periodical, cycle, status…" />
      {loading && <div className="ca-skeleton" />}
      <div className="sv-cards">
        {list.map((it) => (
          <div key={it[0]} className="sv-card" onClick={() => onUse(draftFromPeriodical(it, sites))}>
            <div className="sv-card-name">{it[4]}</div>
            <div className="sv-card-line">{it[1]}</div>
            <div className="sv-card-line sv-card-muted">{it[2] ? fmtShort(it[2]) : ''}{it[3] && it[3] !== it[2] ? ` – ${fmtShort(it[3])}` : ''} · {it[19]} · {it[21]}</div>
            <div className="sv-card-foot"><span className="sv-card-muted">Attending: {it[13] || '—'}</span><UseJobBtn onClick={() => onUse(draftFromPeriodical(it, sites))} /></div>
          </div>
        ))}
        {!loading && list.length === 0 && <div className="ca-no-jobs">No periodicals match.</div>}
      </div>
    </div>
  );
};

/* ── 4. Ticket ───────────────────────────────────────────────── */
const TicketPanel = ({ sites, onUse }) => {
  const { monthYear, setMonthYear, items, loading } = useMonthData(fetchTickets, 'tickets');
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items
      .filter((t) => t[7] !== 'Ticket Completed')
      .filter((it) => !n || [it[1], it[2], it[7], it[9], it[11]].some((v) => String(v || '').toLowerCase().includes(n)));
  }, [items, q]);
  return (
    <div className="sv-panel">
      <div className="sv-panel-title">Open tickets <span className="sv-day-count">{list.length}</span></div>
      <MonthNav value={monthYear} onChange={setMonthYear} />
      <Search value={q} onChange={setQ} placeholder="Search by client, type, subject, status…" />
      {loading && <div className="ca-skeleton" />}
      <div className="sv-cards">
        {list.map((it) => (
          <div key={it[0]} className="sv-card" onClick={() => onUse(draftFromTicket(it, sites))}>
            <div className="sv-card-name">{it[9] || 'No subject'}</div>
            <div className="sv-card-line">{it[1]} · {it[2]}</div>
            <div className="sv-card-line sv-card-muted">Created {it[3] ? fmtShort(it[3]) : '—'} · {it[7]}</div>
            <div className="sv-card-foot"><span className="sv-card-muted">Cleaner: {it[11] || 'Unassigned'}</span><UseJobBtn onClick={() => onUse(draftFromTicket(it, sites))} /></div>
          </div>
        ))}
        {!loading && list.length === 0 && <div className="ca-no-jobs">No open tickets match.</div>}
      </div>
    </div>
  );
};

/* ── job draft form (after Use Job) ─────────────────────────── */
const JobDraftForm = ({ draft, onSave, onCancel }) => {
  const [f, setF] = useState({ ...draft, jobSuburbLabel: draft.jobSuburb ? suburbLabel(draft.jobSuburb) : '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const sub = findSuburb(f.jobSuburbLabel);
  const endOpts = TIME_LABELS.filter((l) => !f.jobStart || labelToTime(l) > f.jobStart);

  const save = async () => {
    if (busy) return;
    setErr('');
    if (!f.jobName.trim()) { setErr('Job name is required'); return; }
    if (f.jobStart && f.jobEnd && f.jobEnd <= f.jobStart) { setErr('Job end must be after job start'); return; }
    setBusy(true);
    try {
      await onSave({
        jobName: f.jobName, jobType: f.jobType, jobAddress: f.jobAddress,
        jobSuburb: sub?.s || '', jobPostcode: sub?.p || '', jobLat: sub?.la, jobLng: sub?.lo,
        jobStart: f.jobStart, jobEnd: f.jobEnd, jobStartDate: f.jobStartDate,
        contactPerson: f.contactPerson, contactMobile: f.contactMobile, notes: f.notes,
        sourceRecordId: f.sourceRecordId, sourceTable: f.sourceTable,
      });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="sv-panel sv-draft">
      <div className="sv-panel-title">Job details <span className="ca-avail-status open">{f.jobType}</span></div>
      <div className="sv-form">
        <label className="sv-field sv-wide"><span className="ca-label">Job name (site)</span><input value={f.jobName} onChange={(e) => set('jobName')(e.target.value)} /></label>
        <label className="sv-field sv-wide">
          <span className="ca-label">Job address</span>
          <input value={f.jobAddress} onChange={(e) => { set('jobAddress')(e.target.value); const g = guessSuburb(e.target.value); if (g) set('jobSuburbLabel')(suburbLabel(g)); }} placeholder="Street, suburb" />
        </label>
        <div className="sv-field ob-field"><CustomSelect label="Job suburb" value={f.jobSuburbLabel} onChange={set('jobSuburbLabel')} options={SUBURB_LABELS} /></div>
        <label className="sv-field"><span className="ca-label">Job start date</span><input type="date" value={f.jobStartDate || ''} min={melTodayISO()} onChange={(e) => set('jobStartDate')(e.target.value)} /></label>
        <div className="sv-field ob-field"><CustomSelect label="Job start" value={timeToLabel(f.jobStart)} onChange={(l) => setF((p) => ({ ...p, jobStart: labelToTime(l), jobEnd: p.jobEnd && p.jobEnd <= labelToTime(l) ? '' : p.jobEnd }))} options={TIME_LABELS} /></div>
        <div className="sv-field ob-field"><CustomSelect label="Job end" value={timeToLabel(f.jobEnd)} onChange={(l) => set('jobEnd')(labelToTime(l))} options={endOpts} /></div>
        <label className="sv-field"><span className="ca-label">Contact person</span><input value={f.contactPerson} onChange={(e) => set('contactPerson')(e.target.value)} /></label>
        <label className="sv-field"><span className="ca-label">Contact mobile</span><input value={f.contactMobile} onChange={(e) => set('contactMobile')(e.target.value)} inputMode="tel" /></label>
        <label className="sv-field sv-wide"><span className="ca-label">Notes — what the job is and how to do it</span><textarea rows={4} value={f.notes} onChange={(e) => set('notes')(e.target.value)} /></label>
        {err && <div className="ca-error sv-wide">{err}</div>}
      </div>
      <div className="sv-panel-foot">
        <button type="button" className="ca-btn ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className={`ca-btn solid ${busy ? 'is-busy' : ''}`} style={{ background: ACCENT }} disabled={busy} aria-busy={busy} onClick={save}>
          {busy && <span className="sv-spin" aria-hidden="true" />}{busy ? 'Saving…' : 'Add to Jobs Available'}
        </button>
      </div>
    </div>
  );
};

/* ── Casual jobs feed: pipeline job + its assignments + the casual's availability ──
   Grouped by date (the job's start date, else the assignment date). */
const JOB_STATUS = { pending: 'Pending', accepted: 'Accepted', declined: 'Declined', approved: 'Approved', cancelled: 'Cancelled' };
const AVAIL_STATUS = { Open: 'open', Assigned: 'assigned', Withdrawn: 'withdrawn', Expired: 'expired' };

const buildFeed = (snlJobs, assignments, slots) => {
  const slotById = new Map(slots.map((sl) => [sl.id, sl]));
  const byPipeline = new Map();
  assignments.forEach((a) => {
    const key = a.pipelineJobId || `name:${a.jobName}`;
    if (!byPipeline.has(key)) byPipeline.set(key, []);
    byPipeline.get(key).push({ ...a, slot: a.availabilityId ? slotById.get(a.availabilityId) || null : null });
  });
  const rows = snlJobs.map((j) => {
    const list = byPipeline.get(j.id) || byPipeline.get(`name:${j.jobName}`) || [];
    list.sort((x, y) => (x.date || '').localeCompare(y.date || '') || (x.jobStart || '').localeCompare(y.jobStart || ''));
    const date = j.jobStartDate || list[0]?.date || '';
    return { job: j, assignments: list, date };
  });
  const groups = new Map();
  rows.forEach((r) => { const k = r.date || 'No date'; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
  return [...groups.entries()].sort((a, b) => (a[0] === 'No date' ? 1 : b[0] === 'No date' ? -1 : a[0].localeCompare(b[0])));
};

const jobStatusCls = (st) => (st === 'Available' ? 'open' : st === 'Assigned' ? 'assigned' : 'expired');

/* record modal for a pipeline job: every field + its assignments + availability */
const FeedRecordModal = ({ row, onClose, onCloseJob, onEditJob, onDuplicate, onOpenAssignment }) => {
  const { job, assignments } = row;
  const approved = assignments.some((a) => a.status === 'approved');
  const canEdit = !!onEditJob && job.status !== 'Closed' && !approved;      // hours + notes, until a casual is Approved
  const [editing, setEditing] = useState(false);
  const freshEdit = () => ({ jobStart: job.jobStart || '', jobEnd: job.jobEnd || '', notes: job.notes || '' });
  const [ef, setEf] = useState(freshEdit);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const saveEdit = async () => {
    if (busy) return;
    setErr('');
    if (ef.jobStart && ef.jobEnd && ef.jobEnd <= ef.jobStart) { setErr('Job end must be after job start'); return; }
    setBusy(true);
    try { await onEditJob(job.id, { jobStart: ef.jobStart, jobEnd: ef.jobEnd, notes: ef.notes }); setEditing(false); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  const F = ({ label, value, wide }) => (
    <div className={`sv-detail-field ${wide ? 'sv-wide' : ''}`}><span className="ca-label">{label}</span><div className="sv-detail-value">{value === '' || value == null ? <span className="sv-muted">—</span> : value}</div></div>
  );
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec sv-assign sv-assign-modal sv-detail" role="dialog" aria-modal="true" aria-label="Job record">
        <div className="ca-rec-head">
          <div className="ca-rec-title">{job.jobName}<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{job.jobStartDate ? fmtShort(job.jobStartDate) : 'No date'}</span></div>
          <div className="ca-rec-sub"><span className="sv-type-pill">{job.jobType}</span><span className="ca-rec-km">added by {job.createdBy || '—'}</span></div>
          <span className={`ca-avail-status ${jobStatusCls(job.status)} ca-rec-status`}>{job.status}</span>
        </div>
        <div className="sv-assign-scroll">
        <div className="ca-rec-body sv-detail-grid">
          {err && <div className="ca-error sv-wide">{err}</div>}
          <F label="Job address" value={addrLine(job.jobAddress, job.jobSuburb)} wide />
          {editing ? (
            <>
              {/* strict format: the same 30-minute pickers the draft form uses */}
              <div className="sv-field ob-field"><CustomSelect label="Job start" value={timeToLabel(ef.jobStart)} onChange={(l) => setEf((p) => ({ ...p, jobStart: labelToTime(l), jobEnd: p.jobEnd && p.jobEnd <= labelToTime(l) ? '' : p.jobEnd }))} options={TIME_LABELS} /></div>
              <div className="sv-field ob-field"><CustomSelect label="Job end" value={timeToLabel(ef.jobEnd)} onChange={(l) => setEf((p) => ({ ...p, jobEnd: labelToTime(l) }))} options={TIME_LABELS.filter((l) => !ef.jobStart || labelToTime(l) > ef.jobStart)} /></div>
            </>
          ) : (
            <>
              <F label="Job start" value={timeToLabel(job.jobStart)} />
              <F label="Job end" value={timeToLabel(job.jobEnd)} />
            </>
          )}
          <F label="Contact person" value={job.contactPerson} />
          <F label="Contact mobile" value={job.contactMobile} />
          {editing
            ? <label className="sv-field sv-wide"><span className="ca-label">Notes</span><textarea rows={4} value={ef.notes} onChange={(e) => setEf((p) => ({ ...p, notes: e.target.value }))} /></label>
            : <F label="Notes" value={job.notes ? <span style={{ whiteSpace: 'pre-line' }}>{job.notes}</span> : ''} wide />}
          <F label="Source" value={job.sourceTable ? `${job.sourceTable}${job.sourceRecordId ? ` · ${job.sourceRecordId}` : ''}` : ''} wide />
        </div>
        <div className="ca-rec-body sv-rec-assign">
          <div className="sv-panel-title">Casuals <span className="sv-day-count">{assignments.length}</span></div>
          {assignments.length === 0 && <div className="ca-no-jobs">Not assigned yet — use Open Slots to assign a casual.</div>}
          {assignments.map((a) => (
            <div key={a.id} className="sv-rec-assign-row" onClick={() => onOpenAssignment(a)} role="button" tabIndex={0}>
              <div>
                <div className="sv-job-name">{a.casualName || '—'}</div>
                <div className="sv-td-sub">
                  {[a.date ? fmtShort(a.date) : '', cleanerTimes(a) ? `available ${cleanerTimes(a)}` : '',
                    a.slotLocation ? `${a.slotLocation}${a.slotRadius != null ? `, ${a.slotRadius} km` : ''}` : ''].filter(Boolean).join(' · ')}
                </div>
                <div className="sv-td-sub">job {timeToLabel(a.jobStart)} – {timeToLabel(a.jobFinish)}{a.slot ? ` · day ${a.slot.status}` : ''}</div>
              </div>
              <span className={`ca-status ${a.status}`}>{JOB_STATUS[a.status] || a.status}</span>
            </div>
          ))}
        </div>
        <div className="sv-rec-help">
          {job.status === 'Available' && 'Available — this job can be assigned from Open Slots.'}
          {job.status === 'Assigned' && 'Held by an active assignment. To give it to someone else, withdraw that assignment on the Responses tab; the job returns here as Available.'}
          {job.status === 'Closed' && 'Closed — hidden from the pipeline. Duplicate New starts a fresh job from these details.'}
        </div>
        </div>
        <div className="ca-rec-foot">
          {/* Edit times & notes as a footer button, like the other modals; the record stays open after a save */}
          {canEdit && !editing && (
            <button type="button" className="ca-btn sv-foot-left" title="Change the job's hours and notes — a casual already on it gets the new values" onClick={() => setEditing(true)}>Edit times &amp; notes</button>
          )}
          {editing && (
            <>
              <button type="button" className="ca-btn ghost sv-foot-left" disabled={busy} onClick={() => { setEditing(false); setErr(''); setEf(freshEdit()); }}>Cancel</button>
              <button type="button" className={`ca-btn solid ${busy ? 'is-busy' : ''}`} style={{ background: ACCENT }} disabled={busy} aria-busy={busy} onClick={saveEdit}>
                {busy && <span className="sv-spin" aria-hidden="true" />}{busy ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
          {job.status === 'Closed' ? (
            /* a closed job is not reopened — it is duplicated into a fresh draft the supervisor edits and adds */
            onDuplicate && <button type="button" className="ca-btn accept" title="Start a new job pre-filled from this one" onClick={() => { onDuplicate(job); onClose(); }}>Duplicate New</button>
          ) : (
            /* Withdrawn hides the job and takes back any live assignment — gone once a casual is Approved */
            !approved && <button type="button" className="ca-btn decline" title="No longer needed — hides the job and withdraws any live assignment" onClick={() => {
              const active = assignments.filter((a) => ['pending', 'accepted'].includes(a.status)).length;
              const ok = window.confirm(active
                ? `Withdraw this job? ${active} live assignment${active === 1 ? '' : 's'} will be withdrawn and the casual's day freed. (To simply give the job to a different casual, use Withdraw on the Responses tab instead.)`
                : 'Withdraw this job? It will be hidden behind Closed Jobs.');
              if (ok) { onCloseJob(job.id, 'Job withdrawn by supervisor'); onClose(); }
            }}>Withdrawn</button>
          )}
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* the compact rows of one date group — the pipeline view, and the folded peek of Closed Jobs */
const FeedTable = ({ rows, onOpen }) => (
  <div className="sv-table-wrap">
    <table className="sv-table sv-feed">
      <thead>
        <tr><th>Job</th><th>Job Times</th><th className="sv-th-casual">Casual</th><th>Cleaner Times</th><th>Status</th><th>Job Status</th></tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const { job, assignments: list } = row;
          const first = list[0];
          return (
            <React.Fragment key={job.id}>
              <tr className="sv-tr" onClick={() => onOpen(row)}>
                <td data-label="Job"><div className="sv-td-name">{job.jobName}</div></td>
                {/* job → its hours → who's on it → their window: reads left to right */}
                <td data-label="Job Times">{job.jobStart ? `${timeToLabel(job.jobStart)} – ${timeToLabel(job.jobEnd)}` : <span className="sv-muted">—</span>}</td>
                {/* casual names run to 28 characters — the column is sized so any of them fits on one line */}
                <td data-label="Casual" className="sv-td-casual">{list.length === 0 ? <span className="sv-muted">Not assigned yet</span> : list.length === 1 ? first.casualName : `${first.casualName} +${list.length - 1}`}</td>
                {/* the casual's window times — the day itself is the group heading */}
                <td data-label="Cleaner Times">{cleanerTimes(first) || <span className="sv-muted">—</span>}</td>
                <td data-label="Status">
                  {first ? <span className={`ca-status ${first.status}`}>{JOB_STATUS[first.status] || first.status}</span> : <span className="sv-muted">—</span>}
                  {first && ['declined', 'cancelled'].includes(first.status) && job.status === 'Available' && <div className="sv-td-sub">reassign from Open Slots</div>}
                </td>
                <td data-label="Job Status"><span className={`ca-avail-status ${jobStatusCls(job.status)}`}>{job.status}</span></td>
              </tr>
              <tr className="sv-tr-foot" onClick={() => onOpen(row)}>
                <td colSpan={6}>
                  <div className="sv-foot-row">
                    <span className="ca-view-link">Click here to view record<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg></span>
                    {job.jobType && <span className="sv-type-pill sv-foot-type">{job.jobType}</span>}   {/* the job type, after the link */}
                  </div>
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

/* one closed job, every field — the open state of a Closed Jobs fold */
const JobCard = ({ row, onOpen }) => {
  const { job, assignments } = row;
  const F = ({ label, value, wide }) => (
    <div className={`sv-detail-field ${wide ? 'sv-wide' : ''}`}><span className="ca-label">{label}</span><div className="sv-detail-value">{value === '' || value == null ? <span className="sv-muted">—</span> : value}</div></div>
  );
  return (
    <article className="sv-jobcard">
      <div className="sv-jobcard-head">
        <div className="sv-jobcard-name">{job.jobName || 'Job'}</div>
        <span className="sv-type-pill">{job.jobType || '—'}</span>
        <span className={`ca-avail-status ${jobStatusCls(job.status)}`}>{job.status}</span>
      </div>
      <div className="sv-detail-grid">
        <F label="Job address" value={addrLine(job.jobAddress, job.jobSuburb)} wide />
        <F label="Date" value={job.jobStartDate ? fmtShort(job.jobStartDate) : ''} />
        <F label="Hours" value={job.jobStart ? `${timeToLabel(job.jobStart)} – ${timeToLabel(job.jobEnd)}` : ''} />
        <F label="Contact person" value={job.contactPerson} />
        <F label="Contact mobile" value={job.contactMobile} />
        <F label="Notes" value={job.notes ? <span style={{ whiteSpace: 'pre-line' }}>{job.notes}</span> : ''} wide />
        <F label="Source" value={job.sourceTable ? `${job.sourceTable}${job.sourceRecordId ? ` · ${job.sourceRecordId}` : ''}` : ''} />
        <F label="Added by" value={job.createdBy} />
        <F label="Casuals" wide value={assignments.length === 0 ? '' : (
          <div className="sv-jobcard-casuals">
            {assignments.map((a) => <span key={a.id} className="sv-jobcard-casual">{a.casualName || '—'}{cleanerTimes(a) && <span className="sv-td-sub">{cleanerTimes(a)}</span>} <span className={`ca-status ${a.status}`}>{JOB_STATUS[a.status] || a.status}</span></span>)}
          </div>
        )} />
      </div>
      <button type="button" className="ca-link sv-jobcard-open" onClick={() => onOpen(row)}>Open record ›</button>
    </article>
  );
};

const JobsFeed = ({ snlJobs, assignments, slots, loading, today, onClose, onEdit, onDuplicate, onResync, title = 'Casual jobs', cards = false }) => {
  const groups = useMemo(() => buildFeed(snlJobs, assignments, slots), [snlJobs, assignments, slots]);
  const [open, setOpen] = useState(null);           // feed row (the one clicked)
  // the record reads the CURRENT row for that job, not the snapshot it was opened with — so an edit,
  // a withdraw or an assignment landing while it is open shows straight away instead of on reopen
  const openRow = open ? (groups.flatMap(([, rows]) => rows).find((r) => r.job.id === open.job.id) || open) : null;
  const [detail, setDetail] = useState(null);       // one assignment
  const [openDate, setOpenDate] = useState(null);                // every date group folded by default; ONE open at a time
  const toggleFold = (d) => setOpenDate((cur) => (cur === d ? null : d));    // opening one closes the other
  const dateLabel = (date) => (date === 'No date' ? 'No date set' : `${date === today ? 'Today · ' : ''}${fmtShort(date)}`);
  return (
    <div className="sv-panel">
      <div className="sv-panel-title">
        {title} <span className="sv-day-count">{snlJobs.length}</span>
        <button type="button" className="ca-link sv-resync" title="Re-derive Job Status and day Status from the assignments on record" onClick={onResync}>Resync statuses</button>
      </div>
      {loading && snlJobs.length === 0 && <div className="ca-skeleton" />}
      {!loading && snlJobs.length === 0 && <div className="ca-no-jobs">{title === 'Closed jobs' ? 'No closed jobs.' : 'Nothing in the pipeline yet — pick a job type above.'}</div>}
      {/* one fold per date: two rows closed; open = every row (pipeline) or a card per job (Closed Jobs) */}
      {groups.map(([date, rows]) => {
        const open = openDate === date;
        return (
          <FoldGroup key={date} title={dateLabel(date)} count={rows.length} accent={ACCENT} open={open} onToggle={() => toggleFold(date)}
                     openChildren={cards ? <div className="sv-fold-cards">{rows.map((row) => <JobCard key={row.job.id} row={row} onOpen={setOpen} />)}</div> : null}>
            <FeedTable rows={open && !cards ? rows : rows.slice(0, FOLD_PEEK)} onOpen={setOpen} />
          </FoldGroup>
        );
      })}
      {openRow && <FeedRecordModal row={openRow} onClose={() => setOpen(null)} onCloseJob={onClose} onEditJob={onEdit} onDuplicate={onDuplicate} onOpenAssignment={setDetail} />}
      {detail && <JobDetailModal j={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Tab component
   ───────────────────────────────────────────────────────────── */
export const CreateCasualJobs = () => {
  const { snlJobs, snlJobsLoading, createSnlJob, closeSnlJob, editSnlJob, resync, responses, roster, slots, today } = useSuper();
  const assignments = useMemo(() => [...responses, ...roster], [responses, roster]);
  const [type, setType] = useState(null);
  const [draft, setDraft] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const visibleJobs = useMemo(() => snlJobs.filter((j) => (showClosed ? j.status === 'Closed' : j.status !== 'Closed')), [snlJobs, showClosed]);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getfetchSites()
      .then((r) => alive && r.status === 'success' && setSites([...(r.activeSites || []), ...(r.nonactiveSites || [])]))
      .catch(() => {})
      .finally(() => alive && setSitesLoading(false));
    return () => { alive = false; };
  }, []);

  const pick = (t) => { setType((cur) => (cur === t ? null : t)); setDraft(null); setShowClosed(false); };
  // after a job is added: clear the draft AND the active button so the feed is the view again
  const save = async (payload) => { await createSnlJob(payload); setDraft(null); setType(null); };
  // Duplicate New from a closed job's record: the draft form opens pre-filled, and the feed
  // goes back to the pipeline so the new job is in view once it is added
  const duplicate = (job) => { setDraft(draftFromJob(job)); setType(null); setShowClosed(false); };

  return (
    <div className="sv-pane sv-create">
      {/* how-to videos for supervisors — first, right under the info bar, then the five job-type cards */}
      <VideoAvailableLibrary leg="supervisor" />

      <div className="sv-jobtypes sv-jobtypes-5">
        {JOB_TYPES.map((t) => (
          <button key={t} type="button" className={`sv-jobtype ${type === t ? 'on' : ''}`} style={{ '--accent': ACCENT }} onClick={() => pick(t)}>
            <span className="sv-jobtype-label">{t}</span>
            <span className="sv-jobtype-hint">
              {t === 'New Client' && 'Create a site, then a job'}
              {t === 'Existing' && 'Pick a site'}
              {t === 'Periodical' && 'From periodical work'}
              {t === 'Ticket' && 'From an open ticket'}
            </span>
          </button>
        ))}
        <button type="button" className={`sv-jobtype sv-jobtype-closed ${showClosed ? 'on' : ''}`} style={{ '--accent': 'rgba(255,255,255,0.45)' }} onClick={() => { setShowClosed((v) => !v); setType(null); setDraft(null); }}>
          <span className="sv-jobtype-label">{CLOSED}</span>
          <span className="sv-jobtype-hint">{showClosed ? 'Showing closed only' : `${snlJobs.filter((j) => j.status === 'Closed').length} hidden`}</span>
        </button>
      </div>

      {type === 'New Client' && !draft && <NewClientPanel onUse={setDraft} onSiteCreated={(s) => setSites((p) => [...p, s])} />}
      {type === 'Existing' && !draft && <ExistingSitesPanel sites={sites} loading={sitesLoading} onUse={setDraft} />}
      {type === 'Periodical' && !draft && <PeriodicalPanel sites={sites} onUse={setDraft} />}
      {type === 'Ticket' && !draft && <TicketPanel sites={sites} onUse={setDraft} />}

      {draft && <JobDraftForm draft={draft} onSave={save} onCancel={() => setDraft(null)} />}

      <JobsFeed snlJobs={visibleJobs} assignments={assignments} slots={slots} loading={snlJobsLoading} today={today} onClose={closeSnlJob} onEdit={editSnlJob} onDuplicate={duplicate} onResync={resync} title={showClosed ? 'Closed jobs' : 'Casual jobs'} cards={showClosed} />
    </div>
  );
};

export default CreateCasualJobs;